import { create } from 'zustand';
import { Schedule, Shift, VacationDay, ScheduleStatus } from '../../types/schedule';
import { scheduleService } from '../api/services/scheduleService';
import { shiftService } from '../api/services/shiftService';
import { vacationService } from '../api/services/vacationService';

// Define the type for schedule creation input
export interface ScheduleInput {
  team_id: string;
  created_by: string;
  start_date: string;
  end_date: string;
  status: ScheduleStatus;
  start_time: string;
  end_time: string;
}

interface ScheduleState {
  // Current schedule
  currentSchedule: Schedule | null;
  shifts: Shift[];
  vacationDays: VacationDay[];
  isLoading: boolean;
  error: Error | null;

  // Schedule list for a team
  teamSchedules: Schedule[];
  isLoadingTeamSchedules: boolean;
  
  // Actions
  fetchSchedule: (id: string) => Promise<void>;
  fetchTeamSchedules: (teamId: string) => Promise<void>;
  createSchedule: (schedule: ScheduleInput) => Promise<Schedule | null>;
  updateScheduleStatus: (id: string, status: ScheduleStatus) => Promise<void>;
  fetchShifts: (scheduleId: string) => Promise<void>;
  assignProviderToShift: (shiftId: string, providerId: string | null) => Promise<void>;
  addVacationDays: (scheduleId: string, vacationDays: VacationDay[]) => Promise<void>;
  removeVacationDay: (scheduleId: string, vacationDay: VacationDay) => Promise<void>;
  fetchVacationDays: (scheduleId: string) => Promise<void>;
  openForPreferences: (id: string, dueDate: string) => Promise<void>;
  closePreferences: (id: string) => Promise<void>;
  publishSchedule: (id: string) => Promise<void>;
}

export const useScheduleStore = create<ScheduleState>((set, get) => ({
  // State
  currentSchedule: null,
  shifts: [],
  vacationDays: [],
  isLoading: false,
  error: null,
  teamSchedules: [],
  isLoadingTeamSchedules: false,
  
  // Actions
  fetchSchedule: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await scheduleService.getScheduleById(id);
      if (error) throw error;
      set({ currentSchedule: data });
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ isLoading: false });
    }
  },
  
  fetchTeamSchedules: async (teamId) => {
    set({ isLoadingTeamSchedules: true, error: null });
    try {
      const { data, error } = await scheduleService.getSchedulesByTeamId(teamId);
      if (error) throw error;
      set({ teamSchedules: data || [] });
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ isLoadingTeamSchedules: false });
    }
  },
  
  createSchedule: async (schedule) => {
    set({ isLoading: true, error: null });
    try {
      console.log('Schedule data being sent to Supabase:', schedule);
      const { data, error } = await scheduleService.createSchedule(schedule);
      
      if (error) {
        console.error('Supabase error creating schedule:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Error details:', error.details);
        throw error;
      }
      
      // Update team schedules if we already have them loaded
      const { teamSchedules } = get();
      if (teamSchedules.length > 0 && data) {
        set({ teamSchedules: [data, ...teamSchedules] });
      }
      
      return data;
    } catch (error) {
      console.error('Error caught in createSchedule:', error);
      set({ error: error as Error });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },
  
  updateScheduleStatus: async (id, status) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await scheduleService.updateScheduleStatus(id, status);
      if (error) throw error;
      
      // Update current schedule if it matches
      const { currentSchedule, teamSchedules } = get();
      if (currentSchedule?.id === id && data) {
        set({ currentSchedule: data });
      }
      
      // Update in team schedules list if present
      if (teamSchedules.length > 0 && data) {
        set({
          teamSchedules: teamSchedules.map(s => 
            s.id === id ? data : s
          )
        });
      }
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ isLoading: false });
    }
  },
  
  fetchShifts: async (scheduleId) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await shiftService.getShiftsByScheduleId(scheduleId);
      if (error) throw error;
      set({ shifts: data || [] });
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ isLoading: false });
    }
  },
  
  assignProviderToShift: async (shiftId, providerId) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await shiftService.assignProviderToShift(shiftId, providerId);
      if (error) throw error;
      
      // Update in shifts list if present
      const { shifts } = get();
      if (shifts.length > 0 && data) {
        set({
          shifts: shifts.map(s => 
            s.id === shiftId ? data : s
          )
        });
      }
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ isLoading: false });
    }
  },
  
  addVacationDays: async (scheduleId, vacationDays) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await vacationService.addVacationDays(scheduleId, vacationDays);
      if (error) throw error;
      
      // Update local vacationDays state
      const { vacationDays: currentVacationDays } = get();
      set({ vacationDays: [...currentVacationDays, ...vacationDays] });
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ isLoading: false });
    }
  },
  
  removeVacationDay: async (scheduleId, vacationDay) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await vacationService.removeVacationDay(scheduleId, vacationDay);
      if (error) throw error;
      
      // Update local vacationDays state
      const { vacationDays } = get();
      set({
        vacationDays: vacationDays.filter(
          day => !(day.providerId === vacationDay.providerId && day.date === vacationDay.date)
        )
      });
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ isLoading: false });
    }
  },
  
  fetchVacationDays: async (scheduleId) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await vacationService.getVacationDays(scheduleId);
      if (error) throw error;
      set({ vacationDays: data || [] });
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ isLoading: false });
    }
  },
  
  openForPreferences: async (id, dueDate) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await scheduleService.openForPreferences(id, dueDate);
      if (error) throw error;
      
      // Update current schedule if it matches
      const { currentSchedule, teamSchedules } = get();
      if (currentSchedule?.id === id && data) {
        set({ currentSchedule: data });
      }
      
      // Update in team schedules list if present
      if (teamSchedules.length > 0 && data) {
        set({
          teamSchedules: teamSchedules.map(s => 
            s.id === id ? data : s
          )
        });
      }
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ isLoading: false });
    }
  },
  
  closePreferences: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await scheduleService.closePreferences(id);
      if (error) throw error;
      
      // Update current schedule if it matches
      const { currentSchedule, teamSchedules } = get();
      if (currentSchedule?.id === id && data) {
        set({ currentSchedule: data });
      }
      
      // Update in team schedules list if present
      if (teamSchedules.length > 0 && data) {
        set({
          teamSchedules: teamSchedules.map(s => 
            s.id === id ? data : s
          )
        });
      }
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ isLoading: false });
    }
  },
  
  publishSchedule: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await scheduleService.publishSchedule(id);
      if (error) throw error;
      
      // Update current schedule if it matches
      const { currentSchedule, teamSchedules } = get();
      if (currentSchedule?.id === id && data) {
        set({ currentSchedule: data });
      }
      
      // Update in team schedules list if present
      if (teamSchedules.length > 0 && data) {
        set({
          teamSchedules: teamSchedules.map(s => 
            s.id === id ? data : s
          )
        });
      }
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ isLoading: false });
    }
  }
})); 